/**
 * CameraSetup - Pantalla de configuración de cámara pre-partida
 * 501 Dart League Online
 * 
 * Se muestra después de aceptar un reto y antes de empezar a jugar.
 * Ambos jugadores deben:
 * 1. Activar su cámara
 * 2. Enfocar la diana
 * 3. Conectar P2P
 * 4. Confirmar que ven la diana del rival
 * 
 * Solo entonces se permite empezar la partida.
 */

import React, { useState, useEffect, useRef } from 'react';

const CameraSetup = ({ 
  webrtc, 
  player1, 
  player2, 
  myPlayerNum, 
  onReady, 
  onCancel 
}) => {
  const [cameraSetupTimer, setCameraSetupTimer] = useState(60);
  const timerRef = useRef(null);

  const {
    localVideoRef,
    remoteVideoRef,
    phase,
    error,
    peerCameraReady,
    dianaStatus,
    setupComplete,
    remoteStreamActive,
    requestCamera,
    confirmDiana,
    requestPeerReset,
    localHasAudio,
    retryLocalAudio,
  } = webrtc;

  // Countdown para setup (60s)
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCameraSetupTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Si el timer llega a 0 sin setup completo → cancelar
  useEffect(() => {
    if (cameraSetupTimer === 0 && !setupComplete) {
      onCancel('Tiempo agotado para configurar la cámara');
    }
  }, [cameraSetupTimer, setupComplete, onCancel]);

  // Cuando ambos confirman → partida lista
  useEffect(() => {
    if (setupComplete) {
      // Pequeña pausa para que ambos vean el mensaje de éxito
      const timer = setTimeout(() => onReady(), 1500);
      return () => clearTimeout(timer);
    }
  }, [setupComplete, onReady]);

  // Auto-solicitar cámara al montar
  useEffect(() => {
    if (phase === 'idle') {
      requestCamera();
    }
  }, [phase, requestCamera]);

  const rivalName = myPlayerNum === 1 ? player2.username : player1.username;
  const [stuckSeconds, setStuckSeconds] = useState(0);

  useEffect(() => {
    const isStuckPhase = phase === 'ready' || phase === 'connecting' || (phase === 'connected' && !remoteStreamActive);
    if (!isStuckPhase || setupComplete) {
      setStuckSeconds(0);
      return;
    }

    const id = setInterval(() => {
      setStuckSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(id);
  }, [phase, remoteStreamActive, setupComplete]);

  // ─── Pasos del setup ───
  const steps = [
    {
      key: 'camera',
      label: 'Activar cámara',
      done: phase === 'ready' || phase === 'connecting' || phase === 'connected' || phase === 'verified',
      active: phase === 'idle' || phase === 'requesting',
    },
    {
      key: 'peer',
      label: 'Esperando rival',
      done: phase === 'connecting' || phase === 'connected' || phase === 'verified',
      active: phase === 'ready' && !peerCameraReady,
    },
    {
      key: 'p2p',
      label: 'Conectando video',
      done: phase === 'connected' || phase === 'verified',
      active: phase === 'connecting' || (phase === 'ready' && peerCameraReady),
    },
    {
      key: 'diana',
      label: 'Verificar diana',
      done: phase === 'verified',
      active: phase === 'connected',
    },
  ];

  return (
    <div className="camera-setup-overlay">
      <div className="camera-setup-container">
        {/* Header */}
        <div className="camera-setup-header">
          <span className="camera-setup-timer">{cameraSetupTimer}s</span>
          <h2>Configuración de Cámara</h2>
          <p className="camera-setup-subtitle">
            Enfoca tu diana para que {rivalName} pueda ver tus tiradas
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="camera-setup-error">
            <span className="camera-setup-error-icon">⚠️</span>
            <p>{error}</p>
            <button className="camera-setup-retry-btn" onClick={requestCamera}>
              Reintentar
            </button>
          </div>
        )}

        {/* Videos */}
        <div className="camera-setup-videos">
          {/* Mi cámara */}
          <div className="camera-setup-video-box local">
            <div className="camera-setup-video-label">
              <span className="camera-setup-video-dot local"></span>
              Tu cámara
              {localHasAudio === false && (
                <span
                  className="camera-setup-audio-badge no-audio clickable"
                  onClick={retryLocalAudio}
                  title="Pulsa para intentar activar el micrófono"
                >
                  🔇 Activar micro
                </span>
              )}
              {localHasAudio === true && (
                <span className="camera-setup-audio-badge has-audio">🎙️ Micro activo</span>
              )}
            </div>
            <div className="camera-setup-video-wrapper">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="camera-setup-video"
              />
              {(phase === 'idle' || phase === 'requesting') && (
                <div className="camera-setup-video-placeholder">
                  <div className="camera-setup-spinner"></div>
                  <p>Activando cámara...</p>
                </div>
              )}
            </div>
          </div>

          {/* Cámara del rival */}
          <div className="camera-setup-video-box remote">
            <div className="camera-setup-video-label">
              <span className="camera-setup-video-dot remote"></span>
              Diana de {rivalName}
            </div>
            <div className="camera-setup-video-wrapper">
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="camera-setup-video"
              />
              {phase !== 'connected' && phase !== 'verified' && (
                <div className="camera-setup-video-placeholder">
                  {!peerCameraReady ? (
                    <>
                      <div className="camera-setup-spinner"></div>
                      <p>Esperando a {rivalName}...</p>
                    </>
                  ) : (
                    <>
                      <div className="camera-setup-spinner"></div>
                      <p>Conectando video...</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Steps progress */}
        <div className="camera-setup-steps">
          {steps.map((step, i) => (
            <div key={step.key} className={`camera-setup-step ${step.done ? 'done' : ''} ${step.active ? 'active' : ''}`}>
              <div className="camera-setup-step-icon">
                {step.done ? '✓' : step.active ? (
                  <div className="camera-setup-step-spinner"></div>
                ) : (i + 1)}
              </div>
              <span className="camera-setup-step-label">{step.label}</span>
            </div>
          ))}
        </div>

        {/* Botón de confirmar diana (solo cuando video conectado) */}
        {phase === 'connected' && !dianaStatus.me && remoteStreamActive && (
          <div className="camera-setup-confirm">
            <p className="camera-setup-confirm-question">
              ¿Puedes ver correctamente la diana de <strong>{rivalName}</strong>?
            </p>
            <button className="camera-setup-confirm-btn" onClick={confirmDiana}>
              ✅ Sí, veo la diana correctamente
            </button>
          </div>
        )}

        {phase === 'connected' && !remoteStreamActive && (
          <div className="camera-setup-waiting">
            <div className="camera-setup-spinner small"></div>
            <p>Conexión hecha, pero aún no llega vídeo real de {rivalName}.</p>
          </div>
        )}

        {stuckSeconds >= 12 && !setupComplete && (
          <div className="camera-setup-error">
            <span className="camera-setup-error-icon">🔄</span>
            <p>La conexión de vídeo está tardando más de lo normal. Puedes reiniciarla sin salir de la partida.</p>
            <button className="camera-setup-retry-btn" onClick={requestPeerReset}>
              Reiniciar vídeo
            </button>
          </div>
        )}

        {/* Esperando confirmación del rival */}
        {phase === 'connected' && dianaStatus.me && !dianaStatus.rival && (
          <div className="camera-setup-waiting">
            <div className="camera-setup-spinner small"></div>
            <p>Esperando a que {rivalName} confirme que ve tu diana...</p>
          </div>
        )}

        {/* Setup completo */}
        {setupComplete && (
          <div className="camera-setup-success">
            <span className="camera-setup-success-icon">🎯</span>
            <p>¡Todo listo! Iniciando partida...</p>
          </div>
        )}

        {/* Botón cancelar */}
        <button className="camera-setup-cancel-btn" onClick={() => onCancel('Cancelado por el jugador')}>
          ✕ Cancelar partida
        </button>
      </div>
    </div>
  );
};

export default CameraSetup;
